<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" :title="computedTitle" @show="onShow"
                   @hidden="onHidden" :content-in-form="false" modal-class="mobile-full-screen"
                   :show-confirmation="showConfirmation" :is-loading="loading"
                   :hide-footer="disableActions" scrollable>

      <div class="row">
        <div class="col-lg-3 col-sm-4 col-xl-2">
          <input-widget :model="model" @keyup.prevent="onPaddleNumberKeyUp" @keydown="onPaddleNumberKeyDown"
                        @blur="onPaddleNumberBlur" attribute="paddle_number" :disabled="!!model.uuid"
                        type="text"/>
        </div>
        <div class="col-lg-3 col-sm-4 col-xl-2">
          <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                        :max="$getMonthCurrent(model.created_at)"
                        :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
        </div>
        <div class="col-lg-3 col-sm-4 col-xl-2" v-if="hasMoreThanOneLanguage">
          <language-code-select-input-widget hint :model="model" attribute="preferred_language_code"/>
        </div>
        <div class="col-lg-3 col-sm-4 col-xl-2">
          <input-widget :model="model" attribute="complaint_status" type="select2" :items="invoiceComplaintStatuses"
                        value-field="value" display-field="${item.key}"
                        :options="{placeholder: $t('Complaint Status')}"/>
        </div>
        <div class="col-lg-3 col-sm-4 col-xl-2" v-if="showDepositShortcutInInvoiceForm">
          <currency-input-widget :currency="currencySymbol" :model="model" attribute="deposit_invoice_payment_amount"
                                 :label="$t('Deposit Amount')" :placeholder="$t('Amount')"
                                 :rules="model.deposit_invoice_payment_type ? 'required' : ''"/>
        </div>
        <div class="col-lg-3 col-sm-4 col-xl-2" v-if="showDepositShortcutInInvoiceForm">
          <input-widget :model="model" attribute="deposit_invoice_payment_type" type="select2" value-field="value"
                        :label="$t('Payment Method')" display-field="${item.key}" :items="paymentMethods"
                        :options="{placeholder: $t('Method')}"
                        :rules="model.deposit_invoice_payment_amount ? 'required' : ''"/>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="card">
            <div class="card-header d-flex align-items-center p-2">
              <div>
                {{ $t('Customer Info') }}
              </div>
            </div>
            <div class="card-body p-2" style="min-height: 5.3em">
              <div v-if="bidder">
                <customer-email-address-info :customer="bidder.customer" show-intra-community-vat-number
                                             :show-address="false"/>
              </div>
              <no-data-available v-else v-show="true" height="4"/>
            </div>
          </div>
        </div>
        <div class="col-md-4" v-if="!model.is_pickup">
          <div class="card">
            <div class="card-header d-flex align-items-center p-2">
              <div>
                {{ $t('Invoice Shipping address') }}
              </div>
              <b-button class="ml-auto p-0" variant="link" v-permission="'change_invoice'"
                        @click="onShippingAddressEditClick">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Edit') }}
              </b-button>
            </div>
            <div class="card-body p-2" style="min-height: 5.3em">
              <div v-if="model.invoice_shipping_address">
                <p v-html="$options.filters.asAddress(model.invoice_shipping_address, false)" class="p-0"></p>
              </div>
              <no-data-available v-else v-show="true" height="4"/>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card">
            <div class="card-header d-flex align-items-center p-2">
              <div>
                {{ $t('Invoice Billing address') }}
              </div>
              <b-button class="ml-auto p-0" variant="link" v-permission="'change_invoice'"
                        @click="onBillingAddressEditClick">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Edit') }}
              </b-button>
            </div>
            <div class="card-body p-2" style="min-height: 5.3em">
              <div v-if="model.invoice_billing_address">
                <p v-html="$options.filters.asAddress(model.invoice_billing_address, false)" class="p-0"></p>
              </div>
              <no-data-available v-else v-show="true" height="4"/>
            </div>
          </div>
        </div>
      </div>

      <b-form @submit.prevent="onSubmit">

        <div class="row mt-4">
          <div class="col-md-auto" v-if="!isAvalaraEnabled">
            <input-widget @change="calculateData()" is-switch attribute="is_exported"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto" v-if="!isAvalaraEnabled">
            <input-widget @change="calculateData()" is-switch
                          attribute="is_registered_business"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto" v-if="!isAvalaraEnabled">
            <input-widget @change="calculateData()" is-switch
                          attribute="is_tax_applied"
                          :model="model" type="checkbox"/>
          </div>
          <template v-if="showInvoiceExtraOptions && showInvoiceExtraOptions.length > 0 && !isAvalaraEnabled">
            <div class="col-md-auto" v-for="input in showInvoiceExtraOptions"
                 :key="`invoice-modal-dynamic-checkboxes-${input.key}`">
              <input-widget @change="calculateData()" is-switch :attribute="input.key" :model="model"
                            type="checkbox" :label="input.label"/>
            </div>
          </template>
          <div class="col-md-auto">
            <input-widget @change="calculateData()" is-switch
                          attribute="is_pickup"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto" v-permission="'change_customerbankaccount'" v-if="hasStripeProvider">
            <input-widget is-switch
                          attribute="exclude_from_auto_payments"
                          :model="model" type="checkbox"/>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-12 d-flex justify-content-between">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">{{ $t('Uninvoiced Lot') }}</h5>
            </div>
            <b-button size="sm" variant="primary" :disabled="selectedUninvoicedItems.size < 1" class="mb-2"
                      @click="moveSelectedUninvoicedToInvoiced()">
              <font-awesome-icon icon="arrow-down"/>
              {{ $t('Move To Invoiced') }}
            </b-button>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table table-responsive">
              <tr>
                <th style="width: 35px">
                  <b-form-checkbox @change="selectAllUninvoicedRows($event)" style="padding-left: 2.01em"
                                   v-model="areAllUninvoicedItemsSelected"/>
                </th>
                <th style="min-width: 100px">{{ $t('Lot') }}</th>
                <th style="min-width: 180px;">{{ $t('Hammer Price') }}</th>
                <th style="min-width: 180px;">{{ $t('Premium') }}</th>
                <th style="min-width: 180px">{{ dynamicTexts.hammerVat }}</th>
                <th style="min-width: 180px">{{ dynamicTexts.premiumVat }}</th>
                <th style="min-width: 150px" v-if="!isAvalaraEnabled">{{ $t('Import Tax') }}</th>
                <th style="min-width: 80px" class="text-center" v-if="!disableActions">{{ $t('Actions') }}</th>
              </tr>
              <template v-if="model.uninvoiced_financial_items.length > 0">
                <tr :key="`uninvoiced-lot-${item.uuid}`" v-for="(item, index) in model.uninvoiced_financial_items">
                  <td>
                    <b-form-checkbox style="padding-left: 2.01em"
                                     @change="onUninvoicedRowSelect($event, item)"
                                     :checked="selectedUninvoicedItems.has(item)"/>
                  </td>
                  <td>
                    <clickable-lot-no :obj="item" is-financial-item/>
                  </td>
                  <td>
                    <currency-input-widget :currency="currencySymbol" :model="item" attribute="hammer_price"
                                           @input="calculateData()" :label="false"/>
                  </td>
                  <td class="pr-4">
                    <currency-input-widget :currency="currencySymbol" :model="item" attribute="premium"
                                           @input="calculateData()" :label="false"/>
                  </td>
                  <td>
                    {{ item.hammer_vat | asCurrency(currencySymbol) }}
                    <span v-if="isAvalaraEnabled">
                      | {{ item.hammer_vat | calculateTaxRate(item.hammer_price) | asPercentage }}
                    </span>
                  </td>
                  <td>
                    {{ item.premium_vat | asCurrency(currencySymbol) }}
                    <span v-if="isAvalaraEnabled">
                      | {{ item.premium_vat | calculateTaxRate(item.premium) | asPercentage }}
                    </span>
                  </td>
                  <td v-if="!isAvalaraEnabled">{{ item.import_tax | asCurrency(currencySymbol) }}</td>
                  <td v-if="!disableActions">
                    <b-tooltip :target="`move-to-invoiced-btn-${index}`" :title="$t('Move To Invoiced')"/>
                    <b-button size="sm" variant="primary" :id="`move-to-invoiced-btn-${index}`"
                              @click="onMoveToInvoicedClick(item)">
                      <font-awesome-icon icon="arrow-down"/>
                    </b-button>
                    <b-tooltip :target="`mark-unsold-btn-${index}`" :title="$t('Mark Unsold')"/>
                    <b-button size="sm" variant="danger" :id="`mark-unsold-btn-${index}`" class="ml-2"
                              @click="onMarkUnsoldClick(item)">
                      <font-awesome-icon icon="ban"/>
                    </b-button>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td colspan="7">
                  <no-data-available :height="3"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-12 d-flex justify-content-between">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">{{ $t('Invoiced Lots') }}</h5>
            </div>
            <b-button size="sm" variant="primary" :disabled="selectedInvoicedItems.size < 1" class="mb-2"
                      @click="moveSelectedInvoicedToUninvoiced()">
              <font-awesome-icon icon="arrow-up"/>
              {{ $t('Move To Uninvoiced') }}
            </b-button>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table table-responsive">
              <tr>
                <th>
                  <b-form-checkbox style="padding-left: 2.01em"
                                   @change="selectAllInvoicedRows($event)"
                                   v-model="areAllInvoicedItemsSelected"/>
                </th>
                <th style="min-width: 100px">{{ $t('Lot') }}</th>
                <th style="min-width: 180px">{{ $t('Hammer Price') }}</th>
                <th style="min-width: 180px">{{ $t('Premium') }}</th>
                <th style="min-width: 190px">{{ dynamicTexts.hammerVat }}</th>
                <th style="min-width: 190px">{{ dynamicTexts.premiumVat }}
                </th>
                <th style="min-width: 150px" v-if="!isAvalaraEnabled">{{ $t('Import Tax') }}</th>
                <th class="text-center" v-if="!disableActions">{{ $t('Actions') }}</th>
              </tr>
              <template v-if="model.invoiced_financial_items.length > 0">
                <tr :key="`invoiced-lot-${lot.uuid}`" v-for="(lot, index) in model.invoiced_financial_items">
                  <td>
                    <b-form-checkbox style="padding-left: 2.01em"
                                     @change="onInvoicedRowSelect($event, lot)"
                                     :checked="selectedInvoicedItems.has(lot)"/>
                  </td>
                  <td>
                    <clickable-lot-no :obj="lot" is-financial-item/>
                  </td>
                  <td>
                    <currency-input-widget :currency="currencySymbol" :model="lot" attribute="hammer_price"
                                           @input="onHammerPriceChange(lot)" :label="false"/>
                  </td>
                  <td class="pr-4">
                    <currency-input-widget :currency="currencySymbol" :model="lot" attribute="premium"
                                           @input="calculateData()" :label="false"/>
                  </td>
                  <td>
                    {{ lot.hammer_vat | asCurrency(currencySymbol) }}
                    <span v-if="isAvalaraEnabled">
                      | {{ lot.hammer_vat | calculateTaxRate(lot.hammer_price) | asPercentage }}
                    </span>
                  </td>
                  <td>
                    {{ lot.premium_vat | asCurrency(currencySymbol) }}
                    <span v-if="isAvalaraEnabled">
                      | {{ lot.premium_vat | calculateTaxRate(lot.premium) | asPercentage }}
                    </span>
                  </td>
                  <td v-if="!isAvalaraEnabled">{{ lot.import_tax | asCurrency(currencySymbol) }}</td>
                  <td class="text-center" v-if="!disableActions">
                    <b-tooltip :target="`move-to-uninvoiced-btn-${index}`" :title="$t('Move To Uninvoiced')"/>
                    <b-button size="sm" variant="primary" :id="`move-to-uninvoiced-btn-${index}`"
                              @click="onMoveToUninvoicedClick(lot)">
                      <font-awesome-icon icon="arrow-up"/>
                    </b-button>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td :colspan="7">
                  <no-data-available :height="3"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Shipping') }}</h5>
            <input-widget @change="onShippingCheckboxChange" :model="model" attribute="enable_shipping" type="checkbox"
                          is-switch
                          :label="false" class="ml-3"/>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table">
              <tr>
                <th style="width: 200px">{{ $t('Amount') }}</th>
                <th style="width: 200px">{{ $t('Tracking Code') }}</th>
                <th style="width: 15%" v-if="!isAvalaraEnabled">{{ $t('Taxable') }}</th>
                <th style="width: 30%">{{ $t('Calculate Shipping Automatically') }}</th>
              </tr>
              <tr>
                <td class="pr-4">
                  <currency-input-widget :currency="currencySymbol" @input="calculateData()" :model="model"
                                         attribute="shipping_amount" type="number" :label="false"
                                         :disabled="!model.enable_shipping || model.shipping_auto_calculation"/>
                </td>
                <td class="pr-4">
                  <input-widget :disabled="!model.enable_shipping"
                                :model="model" attribute="shipping_tracking_code"
                                :label="false"/>
                </td>
                <td v-if="!isAvalaraEnabled">
                  <input-widget @change="calculateData()" :model="model" attribute="shipping_taxable" type="checkbox"
                                :label="false" :disabled="!model.enable_shipping || model.shipping_auto_calculation"/>
                </td>
                <td>
                  <input-widget @change="calculateData(false,true)" :model="model" attribute="shipping_auto_calculation"
                                type="checkbox"
                                :label="false" :disabled="!model.enable_shipping"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Comment') }}</h5>
          </div>
          <div class="col-md-12">
            <input-widget :model="model" attribute="comment" type="textarea" label=""/>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-12">
            <b-button :disabled="!bidder"
                      class="p-0 mb-2" variant="link" @click="onNewMiscellaneousItemClick">
              <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
              {{ $t('Add Miscellaneous Charge') }}
            </b-button>
          </div>
          <div class="col-md-12" v-if="model.miscellaneous_items.length > 0">
            <table class="info-table table table-sm miscellaneous-table">
              <tr>
                <th style="width: 25%">{{ $t('Description') }}</th>
                <th style="width: 25%">{{ $t('Comment') }}</th>
                <th style="width: 20%">{{ $t('Amount') }}</th>
                <th style="width: 20%">{{ $t('Percentage') }}</th>
                <th style="width: 15%" class="text-center" v-if="!isAvalaraEnabled">{{ $t('Taxable') }}</th>
                <th style="width: 15%" class="text-center">{{ $t('Actions') }}</th>
              </tr>
              <tr :key="`miscellaneous-item-${index}`" v-for="(item, index) in model.miscellaneous_items">
                <td class="d-flex pr-3">
                  <input-widget v-if="hasPredefinedMiscCharges" style="flex: 1" :items="invoiceMiscellaneousItemCharges"
                                value-field="key"
                                display-field="${item.value}"
                                @select2Select="onMiscChargeDescriptionDropdownValueChange(item)"
                                type="select2" :model="item" :label="false" attribute="title_dropdown_value"
                                :rules="item.amount ? 'required' : ''"/>
                  <input-widget v-if="item.title_dropdown_value === 'other' || !hasPredefinedMiscCharges"
                                style="flex: 1"
                                :class="{'ml-2': hasPredefinedMiscCharges}"
                                :rules="item.amount ? 'required|max:255' : 'max:255'"
                                :model="item" attribute="title" :label="false"/>
                </td>
                <td class="pr-3">
                  <input-widget value-field="key"
                                display-field="${item.value}"
                                type="text" :model="item" :label="false" attribute="comment"/>
                </td>
                <td class="pr-3">
                  <currency-input-widget :currency="currencySymbol" @input="calculateData()"
                                         :rules="item.title ? 'required' : ''" :model="item"
                                         attribute="amount"
                                         :label="false"/>
                </td>
                <td>
                  <percentage-input-widget :model="item" :disabled="!item.inventory_uuid"
                                           @input="onMiscItemPercentageChange(item)" attribute="percentage"
                                           :label="false"/>
                </td>
                <td class="text-center" v-if="!isAvalaraEnabled">
                  <input-widget @change="calculateData()" attribute="taxable" :model="item" type="checkbox"
                                :label="false"/>
                </td>
                <td class="text-center">
                  <b-tooltip :target="`remove-commission-button-${index}`" :title="$t('Delete')"/>
                  <font-awesome-icon class="text-danger hover-cursor"
                                     :id="`remove-commission-button-${index}`"
                                     @click="onMiscellaneousItemDelete(index)"
                                     style="font-size: 1.5em;margin-top: .3em"
                                     :icon="'times'"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-5 offset-7">
            <invoice-totals-table :model="model" :financial-items="model.invoiced_financial_items"
                                  :hide-shipping-vat="inclusiveTaxForFinancialObjects"
                                  :hide-misc-charge-vat="inclusiveTaxForFinancialObjects"
                                  :skip-misc-charge-vat-from-total="inclusiveTaxForFinancialObjects"
                                  :skip-shipping-vat-from-total="inclusiveTaxForFinancialObjects"
                                  :miscellaneous-items="model.miscellaneous_items" :currency="currencySymbol"/>
          </div>
        </div>

      </b-form>

      <template v-slot:modal-footer>
        <div class="d-flex align-items-center mr-auto text-muted" v-if="isRecalculating">
          <b-spinner class="mr-2"/>
          <h4 class="d-inline-block mb-0">
            {{ $t('Calculating') }}
          </h4>
        </div>
        <cancel-button @click="hideModal" :disabled="loading">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="handleSubmit(onSubmit)" :show-loader="loading"
                       :disabled="!bidder || model.invoiced_financial_items.length === 0">
          {{ $t('Submit') }}
        </submit-button>
      </template>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import InvoiceModel from "./models/Invoice.model";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import BiddersService from "../bidders/BiddersService";
  import CancelButton from "../../core/components/common/CancelButton";
  import SubmitButton from "../../core/components/common/SubmitButton";
  import InvoicesService from "./InvoicesService";
  import {ON_PDF_MODAL_OPEN, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_CLOSE} from "@/plugins/event-bus/event-bus-names";
  import {SOURCE_INVOICES_TABLE} from "../constants";
  import NoDataAvailable from "../../core/components/common/NoDataAvailable";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import _ from 'lodash';
  import MiscellaneousItem from "./models/MiscellaneousItem.model";
  import LiveBidsService from "../live-bids/LiveBidsService";
  import FinancialItemModel from "@/modules/invoices/models/FinancialItem.model";
  import {HTTP_NOT_FOUND} from "@/core/services/http.service";
  import InvoiceTotalsTable from "@/modules/invoices/InvoiceTotalsTable";
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import CustomerEmailAddressInfo from "../../core/components/customer-info/CustomerEmailAddressInfo";
  import PercentageInputWidget from "../../core/widgets/PercentageInputWidget";
  import {InvoiceModalMixin} from "@/modules/invoices/modals/invoiceModal.mixin";
  import CurrencySelectInputWidget from "@/core/widgets/CurrencySelectInputWidget";
  import ClickableLotNo from "@/modules/lots/clickable-lot-no/ClickableLotNo";
  import uuidV4 from 'uuid';
  import {hasPermission} from "@/core/directives/permission";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {
    mapState: globalMapState,
    mapGetters: globalMapGetters,
    mapActions: globalMapActions
  } = createNamespacedHelpers('global');

  export default {
    name: "InvoiceModal",
    mixins: [InvoiceModalMixin],
    components: {
      ClickableLotNo,
      CurrencySelectInputWidget,
      PercentageInputWidget,
      LanguageCodeSelectInputWidget,
      CustomerEmailAddressInfo,
      InvoiceTotalsTable,
      CurrencyInputWidget,
      NoDataAvailable,
      SubmitButton,
      CancelButton,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        modalUuid: uuidV4(),
        customerUuid: null,
        loading: false,
        showConfirmation: true,
        model: new InvoiceModel(),
        bidder: null,
        isRecalculating: false,
        selectedUninvoicedItems: new Set(),
        selectedInvoicedItems: new Set(),
        areAllUninvoicedItemsSelected: false,
        areAllInvoicedItemsSelected: false,
      }
    },
    computed: {
      ...globalMapState({
        invoiceMiscellaneousItemCharges: state => state.invoiceMiscellaneousItemCharges,
        hasMoreThanOneLanguage: state => state.hasMoreThanOneLanguage,
        invoiceComplaintStatuses: state => state.invoiceComplaintStatuses,
        inclusiveTaxForFinancialObjects: state => state.inclusiveTaxForFinancialObjects,
        currentBranch: state => state.currentBranch,
        showDepositShortcutInInvoiceForm: state => state.showDepositShortcutInInvoiceForm,
        showInvoiceExtraOptions: state => state.showInvoiceExtraOptions,
        hasStripeProvider: state => state.hasStripeProvider,
        paymentMethods: state => state.paymentMethods.filter(item => item.value != 'deposit' && item.value != 'auto'),
        shippingConfigEnabled: state => state.shippingConfigEnabled,
        dynamicTexts: state => state.dynamicTexts,
      }),
      ...globalMapGetters([
        'isAvalaraEnabled',
      ]),
      ...mapState({
        modal: state => state.modal,
        tableData: state => state.tableData,
        sale: state => state.modal.sale,
        editObject: state => state.modal.object,
      }),
      computedTitle() {
        if (this.modal.creditNoteWithoutRefundParams) {
          return this.$i18n.t('Recreate Invoice {no}', {no: this.model.invoice_no})
        }
        if (!this.model || !this.model.uuid) {
          return this.$i18n.t('Generate Invoice')
        }
        return this.$i18n.t('Update Invoice {no}', {no: this.model.invoice_no})
      },
      hasPredefinedMiscCharges() {
        return this.invoiceMiscellaneousItemCharges && this.invoiceMiscellaneousItemCharges.length > 1;
      },
      disableActions() {
        return ['cancelled'].includes(this.model.status);
      },
      currencySymbol() {
        if (this.sale && this.sale.currency) {
          return this.sale.currency.symbol;
        }
        return null;
      },
    },
    watch: {
      async editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new InvoiceModel(this.editObject);
          let cfc = this.currentBranch.tax_rate / 100 + 1

          if (this.model.shipping_taxable && this.inclusiveTaxForFinancialObjects) {
            this.model.shipping_amount = Math.round(parseFloat(this.model.shipping_amount * cfc));
          }

          this.bidder = this.model.bidder;
          this.model.paddle_number = this.bidder.paddle_number;
          this.model.miscellaneous_items = this.model.miscellaneous_items.map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = Math.round(parseFloat(item.amount * cfc))
            }
            return new MiscellaneousItem(item)
          });
          for (let lot of this.bidder.lots) {
            if (lot.financial_item.invoice_uuid === null && lot.status === 'sold') {
              this.model.uninvoiced_financial_items.push(new FinancialItemModel({
                ...lot.financial_item,
                lot_no: lot.lot_no,
                lot
              }))
            } else if (this.model.uuid === lot.financial_item.invoice_uuid) {
              this.model.invoiced_financial_items.push(new FinancialItemModel({
                ...lot.financial_item,
                lot_no: lot.lot_no,
                lot
              }))
            }
          }

          if (this.editObject.bidder && this.editObject.bidder.customer) {
            this.customerUuid = this.editObject.bidder.customer.uuid

            if (!this.model.preferred_language_code) {
              this.model.preferred_language_code = this.bidder.customer.preferred_language_code
            }

            this.setDefaultAddresses(
              this.editObject.invoice_billing_address,
              this.editObject.invoice_shipping_address,
            )
          }

        }
      }
    },
    methods: {
      ...mapActions(['hideModal', 'getTableData']),
      ...globalMapActions(['showAddressModal']),
      async onShow() {
        this.showConfirmation = true;
        this.bidder = null;
        this.loading = false;
      },
      onHidden() {
        this.model = new InvoiceModel();
        this.customerUuid = null;
        this.hideModal();
        this.areAllUninvoicedItemsSelected = false;
        this.areAllInvoicedItemsSelected = false;
        this.selectedInvoicedItems = new Set();
        this.selectedUninvoicedItems = new Set();
      },
      onShippingCheckboxChange() {
        this.$nextTick(() => {
          if (!this.model.enable_shipping) {
            this.model.shipping_tracking_code = null;
            this.model.shipping_amount = null;
            this.model.shipping_taxable = false;
            this.model.shipping_auto_calculation = false;
          } else {
            if (this.shippingConfigEnabled) {
              this.model.shipping_auto_calculation = true;
            }
          }
          this.calculateData(false, true);
        })
      },
      onPaddleNumberKeyUp($event) {
        if ($event.keyCode === 13) {
          this.getBidderInfo();
        }
      },
      onPaddleNumberKeyDown($event) {
        if ($event.keyCode === 13) {
          $event.preventDefault();
        }
      },
      onPaddleNumberBlur() {
        if (!this.bidder || this.bidder.paddle_number !== this.model.paddle_number) {
          this.getBidderInfo();
        }
      },
      getBidderInfo: _.debounce(async function () {
        this.model.resetErrors('paddle_number');

        let expand = [
          'lots.inventory.inventory_miscellaneous_items',
          'lots.financial_item',
          'customer.billing_address.country',
          'customer.shipping_address.country',
          'customer.identification',
          'lots.financial_item.buyer_financial_item_charges.charge',
          'lots.financial_item.unsaved_buyer_financial_item_charges.charge',
          'lots.financial_item.buyer_financial_item_charges_to_delete.charge',
          'lots.financial_item.vendor_financial_item_charges_to_delete.charge'
        ];

        if (hasPermission('change_customerbankaccount') && this.hasStripeProvider) {
          expand.push('customer.term')
        }

        expand = expand.join(',');

        let response = await BiddersService.getBidder(this.modal.sale.sale_no, this.model.paddle_number, expand);
        if (response.success) {

          // Skip if the same bidder info was requested
          if (this.bidder && this.bidder.uuid === response.body.uuid) {
            return;
          } else {
            // Reset previous bidder info if new was requested
            this.model.miscellaneous_items = [];
            this.model.uninvoiced_financial_items = [];
            this.model.invoiced_financial_items = [];
          }

          if (!this.model.uuid && hasPermission('change_customerbankaccount') &&
            response.body && response.body.customer && response.body.customer.term && this.hasStripeProvider) {
            this.model.exclude_from_auto_payments = response.body.customer.term.exclude_from_auto_payments;
          }

          this.bidder = response.body;
          this.model.is_pickup = this.bidder.is_pickup;
          for (let lot of this.bidder.lots) {
            if (lot.financial_item.invoice_uuid === null && lot.status === 'sold') {
              this.model.uninvoiced_financial_items.push(new FinancialItemModel({
                ...lot.financial_item,
                lot_no: lot.lot_no,
                lot: lot.lot,
              }))
            }
          }
          if (this.bidder.customer) {
            const customer = this.bidder.customer;
            this.customerUuid = customer.uuid
            this.model.preferred_language_code = customer.preferred_language_code
            this.setDefaultAddresses(customer.billing_address, customer.shipping_address)
            this.$nextTick(() => {
              this.calculateData();
            })
          }
        } else {
          let errorMsg = null;

          if (response.response.status === HTTP_NOT_FOUND) {
            errorMsg = this.$i18n.t("Bidder not found with paddle number {paddle_no}", {paddle_no: this.model.paddle_number});
          }

          this.bidder = null;
          this.model = new InvoiceModel();

          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.response.status === HTTP_NOT_FOUND) {
            this.model.setErrors('paddle_number', [errorMsg])
          }
        }
      }, 500),
      async onSubmit() {
        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy invoice'));
          return;
        }
        this.model.resetErrors();

        if (!this.model.paddle_number) {
          return false;
        }

        let unpaidInvoiceNotificationSuccess = false;
        if (!this.model.uuid || this.modal.creditNoteWithoutRefundParams) {
          unpaidInvoiceNotificationSuccess = await this.$confirm(
            "Would you like to send `New Invoice Notification` to the customer?"
          );
        }

        this.loading = true;
        let response;

        let cfc = this.currentBranch.tax_rate / 100 + 1

        let shippingAmount = this.model.shipping_amount
        if (this.inclusiveTaxForFinancialObjects && this.model.enable_shipping && this.model.shipping_taxable && shippingAmount) {
          shippingAmount = parseFloat(shippingAmount / cfc).toFixed(2)
        }

        let extra_info = {}
        this.showInvoiceExtraOptions.forEach(i => extra_info[i.key] = this.model[i.key])

        let requestData = {
          uuid: this.model.uuid,
          shipping_auto_calculation: this.model.shipping_auto_calculation,
          invoice_billing_address: this.model.invoice_billing_address,
          invoice_shipping_address: this.model.invoice_shipping_address,
          invoice_tax_rate: this.model.tax_rate,
          financial_items: this.model.invoiced_financial_items.map(item => {
            if (item.exclude_tax_for_premium === null) {
              item.exclude_tax_for_premium = false;
            }
            if (item.exclude_tax_for_hammer_price === null) {
              item.exclude_tax_for_hammer_price = false;
            }
            return {
              uuid: item.uuid,
              premium: item.premium,
              hammer_price: item.hammer_price,
              exclude_tax_for_premium: item.exclude_tax_for_premium,
              exclude_tax_for_hammer_price: item.exclude_tax_for_hammer_price,
            }
          }),
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value) && item.amount).map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = parseFloat(item.amount / cfc).toFixed(2);
            }
            if (this.modal.creditNoteWithoutRefundParams) {
              delete item.uuid;
            }
            return item;
          }),
          paddle_number: this.model.paddle_number,
          sale_no: this.sale.sale_no,
          auction_uuid: this.sale.uuid,
          is_exported: this.model.is_exported,
          is_tax_applied: this.model.is_tax_applied,
          is_registered_business: this.model.is_registered_business,
          is_pickup: this.model.is_pickup,
          shipping_amount: this.model.enable_shipping ? shippingAmount : null,
          shipping_taxable: this.model.enable_shipping ? this.model.shipping_taxable : false,
          shipping_tracking_code: this.model.shipping_tracking_code,
          type: 'auction',
          preferred_language_code: this.model.preferred_language_code,
          complaint_status: this.model.complaint_status || null,
          date: this.model.date,
          send_unpaid_invoice_notification: unpaidInvoiceNotificationSuccess,
          comment: this.model.comment,
          deposit_invoice_payment_amount: this.model.deposit_invoice_payment_amount,
          deposit_invoice_payment_type: this.model.deposit_invoice_payment_type || null,
          extra_info
        };

        if (hasPermission('change_customerbankaccount') && this.hasStripeProvider) {
          requestData['exclude_from_auto_payments'] = this.model.exclude_from_auto_payments;
        }

        if (this.modal.creditNoteWithoutRefundParams) {
          requestData.inherited_from_uuid = requestData.uuid;
          requestData.uuid = null;
          requestData.credit_note_without_refund_params = this.modal.creditNoteWithoutRefundParams;
          response = await InvoicesService.createInvoice(requestData);
        } else if (!this.model.uuid) {
          response = await InvoicesService.createInvoice(requestData);
        } else {
          response = await InvoicesService.updateInvoice(this.model.uuid, requestData);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.hideModal();

          if (this.modal.source === SOURCE_INVOICES_TABLE) {
            this.getTableData();
          }

          if (!this.model.uuid && this.$hasPermission('export_invoice')) {
            let invoice = response.body.invoice;
            this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
              title: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no})
            });
            let pdfDataResponse = await InvoicesService.getInvoicePdfData(invoice.uuid);
            if (pdfDataResponse.success) {
              this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
                ...pdfDataResponse.body,
                filename: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no})
              });
            } else {
              let msg = this.$i18n.t("An error occurred during PDF generation");
              if (pdfDataResponse.body && pdfDataResponse.body.non_field_errors) {
                msg = pdfDataResponse.body.non_field_errors
              }
              this.$errorDialog(msg);
              this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
            }
          }
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.body.miscellaneous_items) {
            for (let index in response.body.miscellaneous_items) {
              this.model.miscellaneous_items[index].setMultipleErrors(response.body.miscellaneous_items[index]);
            }
          } else if (response.body.invoice_addresses) {
            this.$errorDialog(this.$i18n.t("Some required fields are missing in the invoice addresses"));
          } else if (response.body && typeof response.body === 'object') {
            this.model.setMultipleErrors(response.body);
          } else {
            this.$errorDialog(this.$i18n.t("An error occurred during invoice generation."));
          }
        }
      },
      onMoveToInvoicedClick(item) {
        this.model.invoiced_financial_items.push(_.cloneDeep(item));
        this.$nextTick(() => {
          this.model.uninvoiced_financial_items = this.model.uninvoiced_financial_items.filter(i => item.uuid !== i.uuid)
          this.areAllUninvoicedItemsSelected = this.selectedUninvoicedItems.size === this.model.uninvoiced_financial_items.length;
          if (!this.model.uninvoiced_financial_items.length) {
            this.areAllUninvoicedItemsSelected = false;
          }
        })
        this.selectedUninvoicedItems.delete(item)
        this.areAllInvoicedItemsSelected = this.selectedInvoicedItems.size === this.model.invoiced_financial_items.length;
        this.onAfterMoveToInvoiced([item.uuid])
        this.calculateData();
      },
      onMoveToUninvoicedClick(item) {
        this.model.uninvoiced_financial_items.push(_.cloneDeep(item));
        this.$nextTick(() => {
          this.model.invoiced_financial_items = this.model.invoiced_financial_items.filter(i => item.uuid !== i.uuid)
          this.areAllInvoicedItemsSelected = this.selectedInvoicedItems.size === this.model.invoiced_financial_items.length;
          if (!this.model.invoiced_financial_items.length) {
            this.areAllInvoicedItemsSelected = false;
          }
        })
        this.selectedInvoicedItems.delete(item)
        this.areAllUninvoicedItemsSelected = this.selectedUninvoicedItems.size === this.model.uninvoiced_financial_items.length;
        this.calculateData();
      },
      moveSelectedUninvoicedToInvoiced() {
        let selectedItemsArray = [...this.selectedUninvoicedItems];
        let movedUuids = []
        selectedItemsArray.map(i => {
          movedUuids.push(i.uuid)
          this.model.invoiced_financial_items.push(_.cloneDeep(i));
          this.$nextTick(() => this.model.uninvoiced_financial_items = this.model.uninvoiced_financial_items.filter(u => i.uuid !== u.uuid));
        })
        this.selectedUninvoicedItems = new Set();
        this.areAllUninvoicedItemsSelected = this.selectedUninvoicedItems.size === this.model.uninvoiced_financial_items.length;
        this.areAllInvoicedItemsSelected = this.selectedInvoicedItems.size === this.model.invoiced_financial_items.length;
        this.onAfterMoveToInvoiced(movedUuids)
        this.calculateData();
      },
      moveSelectedInvoicedToUninvoiced() {
        let selectedItemsArray = [...this.selectedInvoicedItems];
        selectedItemsArray.map(i => {
          this.model.uninvoiced_financial_items.push(_.cloneDeep(i));
          this.$nextTick(() => this.model.invoiced_financial_items = this.model.invoiced_financial_items.filter(u => i.uuid !== u.uuid));
        })
        this.selectedInvoicedItems = new Set();
        this.areAllUninvoicedItemsSelected = this.selectedUninvoicedItems.size === this.model.uninvoiced_financial_items.length;
        this.areAllInvoicedItemsSelected = this.selectedInvoicedItems.size === this.model.invoiced_financial_items.length;
        this.calculateData();
      },
      selectAllUninvoicedRows(checked) {
        this.clearSelectedUninvoicedItems();
        this.areAllUninvoicedItemsSelected = checked;

        if (this.areAllUninvoicedItemsSelected) {
          this.model.uninvoiced_financial_items.forEach(item => this.selectedUninvoicedItems.add(item));
        }
        this.selectedUninvoicedItems = new Set(this.selectedUninvoicedItems);
      },
      clearSelectedUninvoicedItems() {
        this.selectedUninvoicedItems.clear();
        this.selectedUninvoicedItems = new Set([...this.selectedUninvoicedItems]);
        this.areAllUninvoicedItemsSelected = false;
      },
      onUninvoicedRowSelect(checked, lot) {
        if (checked) {
          this.selectedUninvoicedItems.add(lot)
        } else {
          this.selectedUninvoicedItems.delete(lot)
        }
        this.areAllUninvoicedItemsSelected = this.selectedUninvoicedItems.size === this.model.uninvoiced_financial_items.length;
        this.selectedUninvoicedItems = new Set(this.selectedUninvoicedItems);
      },
      selectAllInvoicedRows(checked) {
        this.clearSelectedInvoicedItems();
        this.areAllInvoicedItemsSelected = checked;

        if (this.areAllInvoicedItemsSelected) {
          this.model.invoiced_financial_items.forEach(item => this.selectedInvoicedItems.add(item));
        }
        this.selectedInvoicedItems = new Set(this.selectedInvoicedItems);
      },
      clearSelectedInvoicedItems() {
        this.selectedInvoicedItems.clear();
        this.selectedInvoicedItems = new Set([...this.selectedInvoicedItems]);
        this.areAllInvoicedItemsSelected = false;
      },
      onInvoicedRowSelect(checked, lot) {
        if (checked) {
          this.selectedInvoicedItems.add(lot)
        } else {
          this.selectedInvoicedItems.delete(lot)
        }
        this.areAllInvoicedItemsSelected = this.selectedInvoicedItems.size === this.model.invoiced_financial_items.length;
        this.selectedInvoicedItems = new Set(this.selectedInvoicedItems);
      },
      async onMarkUnsoldClick(item) {
        let success = await this.$confirm(
          this.$i18n.t("Are you sure you want to mark following lot as unsold?"),
          this.$i18n.t('Lot was successfully marked unsold'),
          LiveBidsService.updateLot,
          {uuid: item.lot_uuid, status: 'unsold'}
        );

        if (success) {
          this.model.uninvoiced_financial_items = this.model.uninvoiced_financial_items.filter(item => item.uuid !== item.uuid)
        }
      },
      selectedItems(item, selectedValues) {
        let selectedItems = []
        selectedValues.forEach(i => {
          if (i.uuid === item.uuid) {
            selectedItems.push(item.uuid)
          }
        })
        return selectedItems
      },
      calculateData: _.debounce(async function (recalculatePremium = false, calculateShipping = false) {
        const isFormValid = await this.$refs.observer.validate();
        if (!isFormValid) {
          return;
        }

        this.isRecalculating = true;

        const financial_items = [
          ...this.model.invoiced_financial_items.map(item => {
            return {
              uuid: item.uuid,
              premium: recalculatePremium ? null : parseFloat(item.premium),
              hammer_price: parseFloat(item.hammer_price),
              exclude_tax_for_premium: item.exclude_tax_for_premium,
              exclude_tax_for_hammer_price: item.exclude_tax_for_hammer_price,
            }
          }),
          ...this.model.uninvoiced_financial_items.map(item => {
            return {
              uuid: item.uuid,
              premium: recalculatePremium ? null : parseFloat(item.premium),
              hammer_price: parseFloat(item.hammer_price)
            }
          })
        ];

        let expand = [
          'lot.inventory.inventory_miscellaneous_items',
          'invoice_lots',
          'miscellaneous_items',
          'financial_items',
          'buyer_financial_item_charges.charge',
          'unsaved_buyer_financial_item_charges.charge',
          'buyer_financial_item_charges_to_delete.charge',
          'vendor_financial_item_charges_to_delete.charge'
        ].join(',')

        let shippingAmount = this.model.shipping_amount

        let cfc = this.currentBranch.tax_rate / 100 + 1

        if (this.inclusiveTaxForFinancialObjects && this.model.enable_shipping && this.model.shipping_taxable && shippingAmount) {
          shippingAmount = parseFloat(shippingAmount / cfc).toFixed(2)
        }

        let extra_info = {}
        this.showInvoiceExtraOptions.forEach(i => extra_info[i.key] = this.model[i.key])

        let {success, body} = await InvoicesService.calculateData({
          uuid: this.model.uuid,
          shipping_auto_calculation: this.model.shipping_auto_calculation,
          invoice_billing_address: this.model.invoice_billing_address,
          invoice_shipping_address: this.model.invoice_shipping_address,
          customer_uuid: this.bidder ? this.bidder.customer_uuid : null,
          invoice_tax_rate: this.model.tax_rate,
          financial_items,
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value) && item.amount).map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = parseFloat(item.amount / cfc).toFixed(2)
            }
            return item
          }),
          sale_no: this.sale.sale_no,
          is_exported: this.model.is_exported,
          is_tax_applied: this.model.is_tax_applied,
          is_registered_business: this.model.is_registered_business,
          is_pickup: this.model.is_pickup,
          shipping_amount: this.model.enable_shipping ? shippingAmount : null,
          shipping_taxable: this.model.enable_shipping ? this.model.shipping_taxable : false,
          type: 'auction',
          extra_info,
          invoiced_financial_item_uuids: this.model.invoiced_financial_items.map(item => item.uuid),
        }, expand);

        if (success) {
          if (body.financial_items.length > 0) {
            for (let financialItem of body.financial_items) {
              let itemIndex = this.model.invoiced_financial_items.findIndex(i => i.uuid === financialItem.uuid);
              if (itemIndex >= 0) {
                this.model.invoiced_financial_items[itemIndex] = new FinancialItemModel({
                  ...this.model.invoiced_financial_items[itemIndex],
                  ...financialItem
                });
                continue;
              }
              itemIndex = this.model.uninvoiced_financial_items.findIndex(i => i.uuid === financialItem.uuid);
              if (itemIndex >= 0) {
                this.model.uninvoiced_financial_items[itemIndex] = new FinancialItemModel({
                  ...this.model.uninvoiced_financial_items[itemIndex],
                  ...financialItem
                });
              }
            }
            this.model.invoiced_financial_items = [...this.model.invoiced_financial_items];
            this.model.uninvoiced_financial_items = [...this.model.uninvoiced_financial_items];

            const tmpInvoicedSet = _.cloneDeep(this.selectedInvoicedItems)
            this.selectedInvoicedItems.clear();
            this.model.invoiced_financial_items.forEach(item => {
              if (this.selectedItems(item, tmpInvoicedSet).includes(item.uuid)) {
                this.selectedInvoicedItems.add(item)
              }
            })

            const tmpUninvoicedSet = _.cloneDeep(this.selectedUninvoicedItems)
            this.selectedUninvoicedItems.clear();
            this.model.uninvoiced_financial_items.forEach(item => {
              if (this.selectedItems(item, tmpUninvoicedSet).includes(item.uuid)) {
                this.selectedUninvoicedItems.add(item)
              }
            })
          }

          if (body.miscellaneous_items.length > 0) {
            for (let miscItem of body.miscellaneous_items) {
              let itemIndex;
              if (miscItem.uuid) {
                itemIndex = this.model.miscellaneous_items.findIndex(mi => mi.uuid === miscItem.uuid);
              } else {
                itemIndex = this.model.miscellaneous_items.findIndex(mi =>
                  (mi.title === miscItem.title || mi.title_dropdown_value === miscItem.title) && mi.amount == miscItem.amount
                );
              }

              if (itemIndex >= 0) {

                if (miscItem.taxable && this.inclusiveTaxForFinancialObjects) {
                  miscItem.amount = Math.round(parseFloat(miscItem.amount * cfc))
                }

                if (miscItem.uuid) {
                  this.model.miscellaneous_items[itemIndex] = new MiscellaneousItem({
                    ...this.model.miscellaneous_items[itemIndex],
                    ...miscItem
                  })
                } else {
                  this.model.miscellaneous_items[itemIndex] = new MiscellaneousItem({
                    ...this.model.miscellaneous_items[itemIndex],
                    amount: miscItem.amount,
                    vat: miscItem.vat,
                    taxable: miscItem.taxable,
                    title: miscItem.title,
                  }, true);
                }
              }
            }
            this.model.miscellaneous_items = [...this.model.miscellaneous_items];
          }

          this.model.tax_rate = body.tax_rate;

          if (body.shipping_taxable && this.inclusiveTaxForFinancialObjects) {
            this.model.shipping_amount = Math.round(parseFloat(body.shipping_amount * cfc));
          } else {
            this.model.shipping_amount = body.shipping_amount;
          }
          this.model.shipping_vat = body.shipping_vat;
          this.model.shipping_taxable = body.shipping_taxable;
          if (!calculateShipping) {
            this.model.enable_shipping = !!this.model.shipping_amount;
          }
        } else {
          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(body);
          }
        }

        this.isRecalculating = false;
      }, 500),
      onHammerPriceChange: _.debounce(async function (financialItem) {
        this.calculateMiscChargesWithPercent(financialItem.lot.inventory_uuid)
        await this.calculateData(true)
      }, 500),
      onMiscItemPercentageChange: _.debounce(async function (miscItem) {
        this.calculateMiscChargesWithPercent(miscItem.inventory_uuid)
        await this.calculateData()
      }, 500),
      setTaxTerms(taxTerms) {
        if (taxTerms && Object.keys(taxTerms).length > 0) {
          this.model.invoiced_financial_items.forEach(item => {
            item.exclude_tax_for_premium = taxTerms.exclude_tax_for_premium;
          });
          if (taxTerms.is_economic_nexus_threshold_reached) {
            if (this.model.enable_shipping) {
              this.model.shipping_taxable = true;
            }
            if (this.model.miscellaneous_items.length > 0) {
              this.model.miscellaneous_items.forEach(item => {
                item.taxable = true;
              })
            }
          }
        }
      },
      onAfterMoveToInvoiced(movedUuids = []) {
        const inventoryUuids = []
        for (let item of this.model.invoiced_financial_items) {
          if (!movedUuids.includes(item.uuid)) {
            continue
          }

          if (item.lot.inventory.inventory_miscellaneous_items && item.lot.inventory.inventory_miscellaneous_items.length > 0) {
            for (let miscItem of item.lot.inventory.inventory_miscellaneous_items) {
              this.model.miscellaneous_items.push(new MiscellaneousItem({
                amount: miscItem.amount,
                title: miscItem.title || miscItem.description,
                comment: miscItem.comment,
                taxable: miscItem.taxable,
                percentage: miscItem.percentage,
                inventory_uuid: miscItem.inventory_uuid,
              }, true))
              inventoryUuids.push(miscItem.inventory_uuid)
            }
          }
        }
        for (let uuid of inventoryUuids) {
          this.calculateMiscChargesWithPercent(uuid)
        }
      },
      calculateMiscChargesWithPercent(inventoryUuid) {
        let fi = this.model.invoiced_financial_items.find(fi => fi.lot.inventory_uuid === inventoryUuid)
        if (!fi || !fi.lot.inventory_uuid) {
          return
        }
        for (let i in this.model.miscellaneous_items) {
          let miscItem = this.model.miscellaneous_items[i]
          if (miscItem.inventory_uuid === fi.lot.inventory_uuid && miscItem.percentage !== null && miscItem.percentage) {
            miscItem.amount = parseFloat(parseFloat(fi.hammer_price) * parseFloat((miscItem.percentage / 100))).toFixed(2)
          }
        }

      },
      onMiscChargeDescriptionDropdownValueChange(item) {
        item.title = '';
      },
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .lots-table, .miscellaneous-table {
    /deep/ .form-group {
      margin-top: .2em;
      margin-bottom: .5em;
    }

    tr {
      td {
        vertical-align: middle;
      }
    }
  }
</style>
